// frontend/src/pages/ApartmentUnitsList.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './ApartmentUnitsList.css';
import { useNavigate } from 'react-router-dom';
import ManagerSidebar from '../../../components/Sidebar/ManagerSidebar';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, faTrash, faPlus, faBuilding, 
  faCheckCircle, faTimesCircle, faSearch 
} from '@fortawesome/free-solid-svg-icons';

const ApartmentUnitsList = () => {
  const [units, setUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const unitsPerPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    fetchApartmentUnits();
  }, []);

  const fetchApartmentUnits = async () => {
    try {
      const res = await axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/appartmentUnits/getAll');
      if (res.data.success) {
        setUnits(res.data.data);
        setError('');
      } else {
        setError(res.data.message || 'Failed to fetch apartment units');
      }
    } catch (err) {
      setError('Error connecting to server');
    }
  };

  const handleDelete = async (unitid) => {
    if (!window.confirm('Are you sure you want to delete this apartment unit?')) return;
    try {
      await axios.delete(`https://ikaze-thouse-backend.vercel.app/api/v1/appartmentUnits/delete/${unitid}`);
      fetchApartmentUnits();
    } catch {
      alert('Failed to delete apartment unit');
    }
  };

  // Filter Logic
  const filteredUnits = units.filter(unit => 
    unit.unitname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    unit.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUnit = currentPage * unitsPerPage;
  const indexOfFirstUnit = indexOfLastUnit - unitsPerPage;
  const currentUnits = filteredUnits.slice(indexOfFirstUnit, indexOfLastUnit);
  const totalPages = Math.ceil(filteredUnits.length / unitsPerPage);

  return (
    <div className="dashboard-wrapper">
      <ManagerSidebar />
      
      <main className="main-content">
        <header className="content-header">
          <div className="header-title">
            <h2>Apartment Inventory</h2>
            <p>Manage and monitor all housing units and their occupancy status.</p>
          </div>
          <button className="add-unit-btn" onClick={() => navigate('/add-apartment-unit')}>
            <FontAwesomeIcon icon={faPlus} /> Add New Unit
          </button>
        </header>

        {/* --- QUICK STATS SECTION --- */}
        <section className="stats-container">
          <div className="stat-card">
            <div className="stat-icon blue"><FontAwesomeIcon icon={faBuilding} /></div>
            <div className="stat-data">
              <label>Total Units</label>
              <h3>{units.length}</h3>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green"><FontAwesomeIcon icon={faCheckCircle} /></div>
            <div className="stat-data">
              <label>Available</label>
              <h3>{units.filter(u => u.status === 'Available').length}</h3>
            </div>
          </div>
        </section>

        {/* --- DATA TABLE SECTION --- */}
        <div className="table-card">
          <div className="table-filters">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} />
              <input 
                type="text" 
                placeholder="Search unit or type..." 
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="custom-table-container">
            <table className="advanced-table">
              <thead>
                <tr>
                  <th>Unit Info</th>
                  <th>Floor</th>
                  <th>Rent Price</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUnits.length === 0 ? (
                  <tr><td colSpan="6" className="empty-state">No matching units found</td></tr>
                ) : (
                  currentUnits.map((unit) => (
                    <tr key={unit.unitid}>
                      <td>
                        <div className="unit-name-cell">
                          <span className="unit-title">{unit.unitname}</span>
                          <small>{unit.description || 'No description'}</small>
                        </div>
                      </td>
                      <td><span className="floor-badge">{unit.floornumber}</span></td>
                      <td className="rent-text">${unit.rentamount.toLocaleString()}</td>
                      <td><span className="type-label">{unit.type}</span></td>
                      <td>
                        <span className={`status-pill ${unit.status.toLowerCase()}`}>
                          {unit.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-icons">
                          <button className="icon-btn edit" onClick={() => navigate(`/edit-apartment-unit/${unit.unitid}`)}>
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="icon-btn delete" onClick={() => handleDelete(unit.unitid)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-footer">
            <p>Showing {currentUnits.length} of {filteredUnits.length} Units</p>
            <div className="pager">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
              <span>Page {currentPage} of {totalPages}</span>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApartmentUnitsList;